import React from "react";
import {
  View,
  StyleSheet,
  ImageBackground,
  Text,
  Linking,
  TouchableOpacity,
  Animated,
  Platform,
  UIManager,
} from "react-native";
import {
  AddPhoto2,
  Clock2,
  ImageCarousel,
  Password,
  Phone,
  SmallCarret,
} from "../../../assets";
import {
  FooterLinks,
  GoogleLoginBtn,
  Header,
  Input,
  PrimaryButton,
  SecondaryButton,
  TextLink,
} from "../../common/native";
import global from "../../../styles/globalStyles";
import { CheckBox } from "@rneui/themed";
import { supabase } from "../../../utils/supabase";

if (Platform.OS === "android") {
  if (UIManager.setLayoutAnimationEnabledExperimental) {
    UIManager.setLayoutAnimationEnabledExperimental(true);
  }
}

// TODO: Refactor this component to use the new components and styles
const PatientSignup = () => {
  const MORE_OPTIONS_PANEL_HEIGHT = 180;
  const [signupMode, setSignupMode] = React.useState<"" | "email" | "phone">(
    "",
  );
  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [phoneNumber, setPhoneNumber] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [checked, setChecked] = React.useState<boolean>(false);
  const [showMoreOptions, setShowMoreOptions] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [doneSignUp, setDoneSignUp] = React.useState<boolean>(false);

  const rotateAnimation = React.useRef(new Animated.Value(0)).current;
  const heightAnimation = React.useRef(new Animated.Value(0)).current;

  const toggleMoreOptions = () => {
    Animated.parallel([
      Animated.timing(rotateAnimation, {
        toValue: showMoreOptions ? 0 : 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(heightAnimation, {
        toValue: showMoreOptions ? 0 : MORE_OPTIONS_PANEL_HEIGHT,
        duration: 300,
        useNativeDriver: false,
      }),
    ]).start();

    setShowMoreOptions(!showMoreOptions);
  };

  const rotate = rotateAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "180deg"],
  });

  const handleClickSignUpWithEmailPassword = async () => {
    setErrorMessage("");
    const trimedEmail = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimedEmail)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters long.");
      return;
    }
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (!data || !data.user || !data.user.id) {
        throw error;
      }
      setDoneSignUp(true);
    } catch (error: any) {
      setErrorMessage(error?.message || "Error in Signing Up");
    } finally {
      setLoading(false);
    }
  };

  const openURL = (url: string) => {
    Linking.openURL(url).catch((err) =>
      console.error("Failed to open URL:", err),
    );
  };

  return (
    <ImageBackground
      source={{
        uri: "https://clinic-os.com/user-app-assets/patient-background.png",
      }}
      style={styles.container}
      resizeMode="cover"
    >
      <Header />
      {doneSignUp ? (
        <View style={styles.contentBox}>
          <View
            style={{
              width: "100%",
              maxWidth: 434,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text
              style={[
                global.title,
                { marginHorizontal: "auto", textAlign: "center" },
              ]}
            >
              Registered
            </Text>
            <Text style={global.text}>Check your email to confirm: </Text>
            <Text style={[global.text, global.fontWeight600]}>{email}</Text>
          </View>
        </View>
      ) : (
        <View style={styles.contentBox}>
          <Text
            style={[
              global.title,
              { marginHorizontal: "auto", textAlign: "center" },
            ]}
          >
            Sign Up & Get Started
          </Text>
          <View style={styles.switchPrompt}>
            <Text style={{ fontFamily: "Urbanist600", fontSize: 16 }}>
              Are you a Clinic?
            </Text>
            <TextLink
              to={"/signup"}
              text={"Switch here"}
              style={[global.blue, global.fontSize18]}
            />
          </View>
          <View style={{ marginRight: "auto", gap: 10 }}>
            <Text
              style={[global.text, global.fontWeight600, global.fontSize18]}
            >
              Get access to:
            </Text>
            <View
              style={{
                gap: 10,
                flexDirection: "row",
                justifyContent: "center",
                marginRight: "auto",
                marginLeft: 15,
              }}
            >
              <ImageCarousel />
              <Text
                style={[global.text, global.fontWeight500, global.fontSize16]}
              >
                Before & after images
              </Text>
            </View>
            <View
              style={{
                gap: 10,
                flexDirection: "row",
                justifyContent: "center",
                marginRight: "auto",
                marginLeft: 15,
              }}
            >
              <Clock2 />
              <Text
                style={[global.text, global.fontWeight500, global.fontSize16]}
              >
                Pre & post-op photo timeline
              </Text>
            </View>
            <View
              style={{
                gap: 10,
                flexDirection: "row",
                justifyContent: "center",
                marginRight: "auto",
                marginLeft: 15,
              }}
            >
              <AddPhoto2 />
              <Text
                style={[global.text, global.fontWeight500, global.fontSize16]}
              >
                Tools to track your recovery
              </Text>
            </View>
          </View>

          <View style={styles.featureList}>
            <SecondaryButton
              text={"Phone Number"}
              icon={<Phone />}
              fullWidth
              style={{
                backgroundColor: signupMode === "phone" ? "#C4EEFD" : "#fff",
              }}
              onPress={() =>
                setSignupMode(signupMode === "phone" ? "" : "phone")
              }
            />

            <TouchableOpacity
              style={styles.moreOptionsButton}
              onPress={toggleMoreOptions}
            >
              <Text style={styles.moreOptionsText}>More ways to sign up</Text>
              <Animated.View style={{ transform: [{ rotate }] }}>
                <SmallCarret />
              </Animated.View>
            </TouchableOpacity>

            <Animated.View
              style={[
                styles.moreOptionsContainer,
                {
                  height: heightAnimation,
                  opacity: heightAnimation.interpolate({
                    inputRange: [0, MORE_OPTIONS_PANEL_HEIGHT],
                    outputRange: [0, 1],
                  }),
                },
              ]}
            >
              <View style={styles.dropdownContent}>
                <GoogleLoginBtn
                  name={"Sign Up with Google"}
                  redirectTo={"patient-app"}
                  trackSignupStarted
                />
                <GoogleLoginBtn
                  name={"Sign Up with Apple"}
                  provider="apple"
                  redirectTo={"patient-app"}
                  trackSignupStarted
                />
                <SecondaryButton
                  text={"Email/Password"}
                  icon={<Password />}
                  fullWidth
                  style={{
                    backgroundColor:
                      signupMode === "email" ? "#C4EEFD" : "#fff",
                  }}
                  onPress={() =>
                    setSignupMode(signupMode === "email" ? "" : "email")
                  }
                />
              </View>
            </Animated.View>
          </View>

          {signupMode === "email" && (
            <View style={{ gap: 8, width: "100%" }}>
              <Input
                type="email-address"
                name="Email"
                value={email}
                placeholder="Email"
                onChangeText={setEmail}
                required
              />
              <Input
                type="default"
                name="Password"
                value={password}
                placeholder="Password"
                onChangeText={setPassword}
                secureTextEntry
                required
              />
            </View>
          )}

          {signupMode === "phone" && (
            <View style={{ gap: 8, width: "100%" }}>
              <Input
                type="phone-pad"
                name="Phone Number"
                value={phoneNumber}
                placeholder="Phone Number"
                onChangeText={setPhoneNumber}
                required
              />
              <View style={{ flexDirection: "row" }}>
                <CheckBox
                  checked={checked}
                  onPress={() => setChecked(!checked)}
                  containerStyle={{
                    backgroundColor: "transparent",
                    borderColor: "transparent",
                    padding: 0,
                    margin: 0,
                  }}
                />
                <Text style={styles.termOfServiceText}>
                  I agree to receive SMS passcodes for authentication as
                  outlined in the{" "}
                  <Text
                    style={styles.termOfServiceLink}
                    onPress={() => openURL("https://www.clinicos.ai/tos")}
                  >
                    Terms of Service
                  </Text>
                  {/* {"."} */}
                </Text>
              </View>
            </View>
          )}

          {(signupMode === "email" || signupMode === "phone") && (
            <>
              <Text
                style={[
                  global.errorText,
                  {
                    marginBottom: 5,
                    marginHorizontal: "auto",
                    textAlign: "center",
                  },
                ]}
              >
                {errorMessage}
              </Text>
              <PrimaryButton
                text="Sign Up"
                fullWidth
                onPress={handleClickSignUpWithEmailPassword}
                disabled={
                  (signupMode === "email" && (!email || !password)) ||
                  (signupMode === "phone" && (!phoneNumber || !checked))
                }
                loading={loading}
              />
            </>
          )}
          <View style={styles.accountPrompt}>
            <Text
              style={[global.text, global.fontWeight600, global.fontSize18]}
            >
              Already have an account?
            </Text>
            <TextLink
              to={"/signin"}
              text={"Sign In"}
              style={[global.blue, global.fontSize18]}
            />
          </View>
        </View>
      )}
      <FooterLinks opacityBackground showContactUs={false} />
    </ImageBackground>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%",
    padding: 10,
    gap: 20,
  },
  contentBox: {
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 20,
    padding: 24,
    gap: 10,
    backgroundColor: "rgba(255, 255, 255, 0.80)",
    width: "100%",
    maxWidth: 430,
    marginBottom: "auto",
  },
  title: {
    color: "#00759B",
    fontSize: 36,
    marginBottom: 20,
  },
  featureList: {
    width: "100%",
    gap: 10,
  },
  moreOptionsButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    paddingVertical: 8,
  },
  moreOptionsText: {
    fontFamily: "Urbanist500",
    fontSize: 16,
  },
  moreOptionsContainer: {
    overflow: "hidden",
    width: "100%",
  },
  dropdownContent: {
    gap: 10,
    width: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
  },
  termOfServiceText: {
    fontFamily: "Urbanist500",
    fontWeight: "500",
    fontSize: 16,
  },
  termOfServiceLink: {
    color: "#00B7F4",
    textDecorationColor: "#fff",
  },
  switchPrompt: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 12,
    marginBottom: 20,
  },
  accountPrompt: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 30,
  },
});

export default PatientSignup;
